<template>
  <div
    v-if="!networkError"
    class="project-list"
  >
    <LoaderImg v-if="!projectList" />
    <div v-if="projectList">
      <div
        v-for="(project, index) in projectList"
        :key="index"
      >
        <ProjectTeaser
          :project="project"
          :lang="lang"
          class="project-teaser"
        />
      </div>
      <button
        v-if="showMoreButton"
        @click="getProjectList"
      >
        Load more...
      </button>
    </div>
  </div>
</template>

<script>
  import ProjectTeaser from './ProjectTeaser.vue'
  import LoaderImg from '../components/LoaderImg.vue'
  import LanguageHelper from '../mixins/LanguageHelper.js'

  const PAGE_SIZE = 10

  export default {
    name: 'ProjectsList',
    components: {
      LoaderImg,
      ProjectTeaser,
    },
    mixins: [LanguageHelper],
    props: {
      lang: { type: String, default: 'en' },
      baseUrl: { type: String, default: '' },
    },
    data() {
      return {
        networkError: false,
        projectList: null,
        nextUrl: null,
      }
    },
    computed: {
      showMoreButton() {
        return this.nextUrl !== null
      },
    },
    mounted() {
      this.getProjectList()
    },
    methods: {
      // Core JSON:API (read-only). web_components limits it to node--project and
      // taxonomy_term--references; "teaser_image" is a computed field with the
      // image style URLs.
      firstPageUrl() {
        const params = new URLSearchParams({
          'filter[promote]': '1',
          'sort': '-created',
          'page[limit]': String(PAGE_SIZE),
          'include': 'field_tags',
          'fields[node--project]': 'drupal_internal__nid,title,field_subtitle,body,field_tags,teaser_image',
          'fields[taxonomy_term--references]': 'drupal_internal__tid,name,path',
        })
        return this.langPrefix(`/jsonapi/node/project?${params}`)
      },
      async getProjectList() {
        try {
          // links.next is absolute; keep only path and query, so requests stay on baseUrl.
          const url = this.nextUrl ?? this.firstPageUrl()
          const res = await fetch(this.baseUrl + url, {
            headers: { Accept: 'application/vnd.api+json' },
          })
          if (!res.ok) {
            throw new Error(`JSON:API request failed: ${res.status}`)
          }
          const json = await res.json()
          this.projectList = [...(this.projectList ?? []), ...toProjects(json)]
          const next = json.links?.next?.href
          this.nextUrl = next ? new URL(next).pathname + new URL(next).search : null
        }
        catch (e) {
          this.networkError = true
          throw e
        }
      },
    },
  }

  // JSON:API document → the shape ProjectTeaser renders.
  function toProjects(json) {
    const terms = new Map((json.included ?? [])
      .filter((r) => r.type === 'taxonomy_term--references')
      .map((r) => [r.id, r.attributes]))
    return json.data.map(({ attributes: a, relationships: r }) => ({
      nid: a.drupal_internal__nid,
      title: a.title,
      fieldSubtitle: a.field_subtitle,
      fieldImage: a.teaser_image,
      body: a.body ?? {},
      fieldTags: (r.field_tags?.data ?? [])
        .map((ref) => terms.get(ref.id))
        .filter(Boolean)
        .map((t) => ({
          name: t.name,
          path: t.path?.alias || `/taxonomy/term/${t.drupal_internal__tid}`,
        })),
    }))
  }
</script>

<style>
  .project-list--loading {
    margin: 1em auto;
    width: 3em;
    height: 3em;
    display: block;
  }
</style>
